"use client";

import { merveillesPresidentielles, formatPrice, Product } from "@/lib/data";
import { useCart } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Crown, ShoppingBag } from "lucide-react";
import Image from "next/image";

/* Bandeau défilant des 8 Produits Vedettes — placement : sous le Hero (top du site) */
export function Vedettes() {
  const addItem = useCart((s) => s.addItem);

  const acheter = (p: Product) =>
    addItem({
      id: p.id,
      name: p.name,
      price: p.price,
      image: p.image,
      presidential: true,
    });

  return (
    <section
      id="vedettes"
      aria-label="8 Produits Vedettes"
      className="relative bg-gradient-to-r from-[#0B0804] via-[#171106] to-[#0B0804] border-y-2 border-[#FFD700]/25 py-5 overflow-hidden"
    >
      {/* Liserés drapeau */}
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#006B3F] via-[#FFD700] to-[#CE1126]" />
      <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#CE1126] via-[#FFD700] to-[#006B3F]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 flex items-center gap-4 mb-4">
        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-[#FFD700] to-[#DAA520] text-black text-[10px] sm:text-xs font-black tracking-[0.25em] uppercase shrink-0 shadow-[0_0_25px_rgba(255,215,0,0.35)]">
          <Crown className="w-3.5 h-3.5" />
          8 Produits Vedettes
        </span>
        <span className="hidden md:inline text-white/40 text-xs font-medium tracking-wide">
          La Collection Présidentielle défile pour vous — cliquez pour acquérir
        </span>
      </div>

      {/* Piste défilante : liste dupliquée pour une boucle parfaite */}
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 w-16 sm:w-28 bg-gradient-to-r from-[#0B0804] to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 sm:w-28 bg-gradient-to-l from-[#0B0804] to-transparent z-10" />

        <div className="vedettes-track flex w-max gap-4 px-4 hover:[animation-play-state:paused]">
          {[...merveillesPresidentielles, ...merveillesPresidentielles].map((p, idx) => (
            <div
              key={`${p.id}-${idx}`}
              className="group relative w-60 sm:w-64 shrink-0 rounded-2xl overflow-hidden border border-[#FFD700]/25 bg-gradient-to-b from-[#1C1508] to-[#0E0B05] hover:border-[#FFD700]/70 transition-all duration-300 hover:shadow-[0_10px_40px_-10px_rgba(255,215,0,0.35)]"
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <Image
                  src={p.image}
                  alt={p.name}
                  width={512}
                  height={384}
                  className="object-cover w-full h-full group-hover:scale-108 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0E0B05] via-transparent to-transparent" />
                <span className="absolute top-2.5 left-2.5 px-2 py-0.5 rounded-full text-[8px] font-black tracking-widest bg-gradient-to-r from-[#FFD700] to-[#DAA520] text-black">
                  {p.badge}
                </span>
              </div>
              <div className="p-3.5">
                <p className="text-[8px] font-bold tracking-[0.2em] text-[#C9A227] uppercase truncate">
                  {p.category} · {p.edition}
                </p>
                <h3 className="text-white font-black text-sm mt-1 truncate">{p.name}</h3>
                <div className="mt-2.5 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[#FFD700] font-black text-sm leading-none">
                      {formatPrice(p.price)}
                      <span className="text-[8px] font-bold text-[#C9A227] ml-1">FCFA</span>
                    </p>
                    <p className="text-[8px] text-white/35 mt-0.5">
                      6x {formatPrice(Math.round(p.price / 6))} sans frais
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => acheter(p)}
                    className="h-7 px-2.5 text-[10px] font-black bg-gradient-to-r from-[#FFD700] to-[#DAA520] text-black hover:shadow-[0_0_18px_rgba(255,215,0,0.5)] shrink-0"
                  >
                    <ShoppingBag className="w-3 h-3 mr-0.5" /> Acquérir
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
